import {useEditor, EditorContent} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Heading from '@tiptap/extension-heading'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import CodeBlock from '@tiptap/extension-code-block'
import Underline from '@tiptap/extension-underline'
import {useEffect, useRef, useState} from 'react'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Code,
  Image as ImageIcon,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo,
  Redo,
  Eraser,
  Youtube,
  Minus,
} from 'lucide-react'

type Props = {
  value: string
  onChange: (value: string) => void
}

type MenuButtonProps = {
  onClick: () => void
  isActive?: boolean
  children: React.ReactNode
  title: string
}

const MenuButton = ({onClick, isActive, children, title}: MenuButtonProps) => (
  <button
    type='button'
    onClick={onClick}
    title={title}
    className={`p-2 rounded-md transition-all ${
      isActive ? 'bg-[#008080] text-white' : 'hover:bg-gray-200 text-gray-600'
    }`}
  >
    {children}
  </button>
)

const TiptapEditor = ({value, onChange}: Props) => {
  const lastValueRef = useRef(value)
  const [modal, setModal] = useState<'link' | 'image' | 'video' | null>(null)
  const [modalUrl, setModalUrl] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
      }),
      Heading.configure({levels: [1, 2, 3]}),
      Underline,
      BulletList,
      OrderedList,
      ListItem,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      CodeBlock,
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[650px]',
      },
    },
    content: value,
    onUpdate({editor}) {
      const html = editor.getHTML()
      if (html !== lastValueRef.current) {
        lastValueRef.current = html
        onChange(html)
      }
    },
  })

  useEffect(() => {
    if (!editor || value === lastValueRef.current) return
    lastValueRef.current = value
    editor.commands.setContent(value, {emitUpdate: false})
  }, [value, editor])

  useEffect(() => {
    if (modal && inputRef.current) inputRef.current.focus()
  }, [modal])

  const openModal = (type: 'link' | 'image' | 'video') => {
    setModal(type)
    setModalUrl('')
  }

  const closeModal = () => {
    setModal(null)
    setModalUrl('')
  }

  const handleInsert = () => {
    if (!editor || !modal || !modalUrl.trim()) {
      closeModal()
      return
    }

    if (modal === 'link') {
      editor.chain().focus().setLink({href: modalUrl}).run()
    } else if (modal === 'image') {
      editor.chain().focus().setImage({src: modalUrl}).run()
    } else if (modal === 'video') {
      let embedUrl = ''
      try {
        const parsed = new URL(modalUrl)
        if (parsed.hostname.includes('youtube.com') && parsed.searchParams.get('v')) {
          embedUrl = `https://www.youtube.com/embed/${parsed.searchParams.get('v')}`
        } else if (parsed.hostname === 'youtu.be') {
          embedUrl = `https://www.youtube.com/embed/${parsed.pathname.slice(1)}`
        } else if (parsed.hostname.includes('vimeo.com')) {
          const vimeoId = parsed.pathname.split('/').pop()
          if (vimeoId) embedUrl = `https://player.vimeo.com/video/${vimeoId}`
        }
      } catch {
        closeModal()
        return
      }

      if (embedUrl) {
        const sanitizedUrl = embedUrl.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
        const iframeHtml = `<div class="aspect-video w-full"><iframe src="${sanitizedUrl}" class="w-full h-full" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`
        editor.chain().focus().insertContent(iframeHtml).run()
      }
    }

    closeModal()
  }

  return (
    <div className='border border-gray-300 rounded-lg overflow-hidden shadow-sm'>
      {/* Toolbar */}
      <div className='flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-200 sticky top-0 z-10'>
        <div className='flex items-center gap-1 pr-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({level: 1}).run()}
            isActive={editor.isActive('heading', {level: 1})}
            title='Heading 1'
          >
            <Heading1 className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({level: 2}).run()}
            isActive={editor.isActive('heading', {level: 2})}
            title='Heading 2'
          >
            <Heading2 className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({level: 3}).run()}
            isActive={editor.isActive('heading', {level: 3})}
            title='Heading 3'
          >
            <Heading3 className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 px-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title='Bold'
          >
            <Bold className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title='Italic'
          >
            <Italic className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            title='Underline'
          >
            <UnderlineIcon className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 px-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title='Bullet List'
          >
            <List className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title='Numbered List'
          >
            <ListOrdered className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title='Blockquote'
          >
            <Quote className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title='Horizontal Rule'
          >
            <Minus className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 px-2 border-r border-gray-300'>
          <MenuButton
            onClick={() => openModal('link')}
            isActive={editor.isActive('link')}
            title='Insert Link'
          >
            <LinkIcon className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => openModal('image')}
            title='Insert Image'
          >
            <ImageIcon className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => openModal('video')}
            title='Insert Video'
          >
            <Youtube className='w-4 h-4' />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            title='Code Block'
          >
            <Code className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 px-2'>
          <MenuButton
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
            title='Clear Formatting'
          >
            <Eraser className='w-4 h-4' />
          </MenuButton>
        </div>

        <div className='flex items-center gap-1 pl-2 ml-auto'>
          <button
            type='button'
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className='p-2 hover:bg-gray-200 text-gray-600 disabled:opacity-50'
            title='Undo'
          >
            <Undo className='w-4 h-4' />
          </button>
          <button
            type='button'
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className='p-2 hover:bg-gray-200 text-gray-600 disabled:opacity-50'
            title='Redo'
          >
            <Redo className='w-4 h-4' />
          </button>
        </div>
      </div>

      {/* Editor Content Area */}
      <div className='p-4 bg-white'>
        <EditorContent editor={editor} />
      </div>

      {/* URL Input Modal */}
      {modal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40' onClick={closeModal}>
          <div
            className='bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6'
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className='text-lg font-semibold text-gray-800 mb-4'>
              {modal === 'link' ? 'Insert Link' : modal === 'image' ? 'Insert Image URL' : 'Insert YouTube/Vimeo URL'}
            </h3>
            <input
              ref={inputRef}
              type='text'
              className='w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:border-[#008080]'
              placeholder={
                modal === 'link'
                  ? 'https://example.com'
                  : modal === 'image'
                  ? 'https://example.com/image.jpg'
                  : 'https://youtube.com/watch?v=...'
              }
              value={modalUrl}
              onChange={(e) => setModalUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInsert()
                if (e.key === 'Escape') closeModal()
              }}
            />
            <div className='flex justify-end gap-3 mt-5'>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors'
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-white bg-[#008080] rounded-lg hover:bg-[#007373] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={!modalUrl.trim()}
                onClick={handleInsert}
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TiptapEditor

